import { Modal } from 'antd';
import { onPreview } from './onPreview';
import { compressFile } from './compressFile';
import { fileExtensionManage } from './fileExtensionManage';
import { fileToBase64 } from './fileToBase64';
/**
 * Creates dragger props for picture-card upload with confirm on remove.
 * Shared by petsitting address and owner animal forms.
 * @function
 *
 * @param {function} t - Translation function.
 * @param {function} dispatchAPI - API dispatch function.
 * @param {function} message - Message display function.
 * @param {Array} fileList - Current file list.
 * @param {function} setFileList - Setter for file list.
 * @param {function} deleteFile - Function(id, dispatchAPI, message) to delete file by id.
 * @param {function} [itemRender] - Optional (_, file) => ReactNode for custom item render.
 * @returns {Object} Props for Ant Design Upload.Dragger with listType="picture-card".
 */
export const createPictureCardDraggerProps = (
  t,
  dispatchAPI,
  message,
  fileList,
  setFileList,
  deleteFile,
  itemRender
) => {
  const safeFileList = Array.isArray(fileList) ? fileList : [];

  const props = {
    listType: 'picture-card',

    fileList: safeFileList,

    onPreview: (file) => onPreview(file),

    onRemove: (file) => {
      Modal.confirm({
        title: t('files.modal.confirm_delete_title'),
        content: t('files.modal.confirm_delete_photo'),
        okText: t('files.modal.ok'),
        cancelText: t('files.modal.cancel'),
        okType: 'warning',

        onOk: () => {
          const newFileList = safeFileList.filter(
            (item) => item.uid !== file.uid
          );
          setFileList(newFileList);

          if (file._id) {
            deleteFile(file._id, dispatchAPI, message);
          }
        }
      });
    },

    beforeUpload: async (file) => {
      try {
        const compressed = await compressFile(file);

        if (!compressed) {
          message.error('Compression failed');
          return false;
        }

        const extension = compressed.name.split('.').pop().toLowerCase();

        if (!fileExtensionManage(extension)) {
          message.error('Only JPG or PNG allowed');
          return false;
        }

        const base64 = await fileToBase64(compressed);

        const newFile = {
          uid: `${Date.now()}`,
          name: compressed.name,
          status: 'done',
          url: base64,
          file: compressed
        };

        setFileList((prev) => {
          const current = Array.isArray(prev) ? prev : [];
          return [...current, newFile];
        });
      } catch (err) {
        message.error('Error converting file');
      }

      return false;
    }
  };

  if (itemRender) {
    props.itemRender = itemRender;
  }

  return props;
};
